import {
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { type DashboardExperienceOrderResponse } from '@library/api-dashboard-experience'
import React, { useEffect, useState } from 'react'
import { browserHistory } from 'react-router'

import { OrderTileActions } from './OrderTileSubcomponents/OrderTileActions'
import { OrderTileHeader } from './OrderTileSubcomponents/OrderTileHeader'
import { OrderTileRecipes } from './OrderTileSubcomponents/OrderTileRecipes'
import { ResponsiveManageDeliveryPanel } from './OrderTileSubcomponents/ResponsiveManageDeliveryPanel'
import { isSkipOrder } from './orderTileState'
import { SkipOrderDiscountModal } from '../SkipOrderDiscountModal'
import { useOrderDeliveryDetails } from '../../hooks/useOrderDeliveryDetails'
import { useOrderTileDeliveryDate } from '../../hooks/useOrderTileDeliveryDate'
import { useOrderTileSkipFlow } from '../../hooks/useOrderTileSkipFlow'
import { useOrderTracking } from '../../hooks/useOrderTracking'

type OrderTileProps = {
  onDeliveryDateSaved?: (selectedDeliveryDate: string) => void
  onSkipSuccess?: (orderId: string) => void
  order: DashboardExperienceOrderResponse
}

const OrderTileContainer = withCitrusTheme(Box, (theme) => [
  theme.responsiveColor(
    [Color.White, Color.White, Color.White, Color.ColdGrey_100],
    ['borderTopColor'],
  ),
  theme.responsiveStyle(
    ['0px', '0px', '0px', '8px'],
    ['borderTopLeftRadius', 'borderTopRightRadius'],
  ),
  {
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
])

export function OrderTile({ onDeliveryDateSaved, onSkipSuccess, order }: OrderTileProps) {
  const {
    trackGetHelpWithThisBoxClick,
    trackOrderExpandClick,
    trackChooseRecipesClick,
    trackEditRecipesClick,
    trackMyOrderClick,
  } = useOrderTracking()
  const [displayedOrder, setDisplayedOrder] = useState(order)
  const [isManageDeliveryExpanded, setIsManageDeliveryExpanded] = useState(false)
  const { orderDeliveryDetails } = useOrderDeliveryDetails(
    displayedOrder.id,
    isManageDeliveryExpanded,
  )
  const { handleSaveDeliveryDate, hasSaveDeliveryDateError, isSavingDeliveryDate } =
    useOrderTileDeliveryDate({
      displayedOrder,
      onDeliveryDateSaved,
      order,
      orderDeliveryDetails,
    })
  const {
    handleApplySkipDiscountClick,
    handleConfirmSkipBoxClick,
    handleRestoreOrderClick,
    handleSkipBoxRequestClick,
    handleSkipDiscountModalClose,
    handleSkipDiscountModalView,
    hasApplySkipDiscountError,
    hasSkipOrderError,
    isApplyingSkipDiscount,
    isFetchingSkipDiscount,
    isRestoringOrder,
    isSkipDiscountModalOpen,
    isSkippingOrder,
    skipDiscount,
  } = useOrderTileSkipFlow({
    order,
    displayedOrder,
    onSkipSuccess,
    setDisplayedOrder,
  })
  const isSkippedOrder = isSkipOrder(displayedOrder)
  const priceKind = typeof displayedOrder.price.kind === 'string' ? displayedOrder.price.kind : null
  const originalPrice =
    typeof displayedOrder.price.original === 'string' ? displayedOrder.price.original : null
  const currentPrice =
    typeof displayedOrder.price.amount === 'string' ? displayedOrder.price.amount : null
  const discountBadgeText =
    typeof displayedOrder.price.discount_label === 'string'
      ? displayedOrder.price.discount_label
      : null

  useEffect(() => {
    setDisplayedOrder(order)
  }, [order])

  const handleGetHelpClick = () => {
    trackGetHelpWithThisBoxClick({
      orderId: displayedOrder.id,
    })
    browserHistory.push(`/get-help?orderId=${displayedOrder.id}`)
  }

  const handleManageDeliveryClick = () => {
    trackOrderExpandClick({
      order: displayedOrder,
    })
    setIsManageDeliveryExpanded((currentValue) => !currentValue)
  }

  const handleChooseRecipesClick = () => {
    if (displayedOrder.state === 'choose') {
      trackChooseRecipesClick({
        order: displayedOrder,
      })
    } else if (displayedOrder.state === 'edit') {
      trackEditRecipesClick({
        order: displayedOrder,
      })
    } else {
      return
    }

    browserHistory.push(`/menu/${displayedOrder.id}`)
  }

  const handleTrackBoxClick = (url: string) => {
    trackMyOrderClick({
      order: displayedOrder,
    })
    window.open(url, '_blank', 'noopener noreferrer')
  }

  return (
    <OrderTileContainer
      data-testid="order-tile"
      width={'100%'}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      borderWidth={0.5}
      borderColor={Color.ColdGrey_100}
      borderStyle={BorderStyle.Solid}
      bg={Color.White}
      style={{ overflow: 'hidden' }}
    >
      <Box paddingV={4} paddingH={4}>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
          <OrderTileHeader
            currentPrice={currentPrice}
            discountBadgeText={discountBadgeText}
            isManageDeliveryExpanded={isManageDeliveryExpanded}
            onManageDeliveryClick={handleManageDeliveryClick}
            order={displayedOrder}
            originalPrice={originalPrice}
            priceKind={priceKind}
          />
          <Space size={[1, 1, 1, 4]} direction="vertical" />
          <OrderTileRecipes
            newAndLimited={displayedOrder.new_and_limited}
            orderId={displayedOrder.id}
            orderStateTitle={displayedOrder.state_title}
            recipes={displayedOrder.recipes}
            topPicks={displayedOrder.top_picks}
          />
          <Space size={4} direction="vertical" />
          <OrderTileActions
            helpCta={displayedOrder.help_cta}
            isManageDeliveryExpanded={isManageDeliveryExpanded}
            isRestoreOrderDisabled={isRestoringOrder}
            onChooseRecipesClick={handleChooseRecipesClick}
            onGetHelpClick={handleGetHelpClick}
            onManageDeliveryClick={handleManageDeliveryClick}
            onRestoreOrderClick={handleRestoreOrderClick}
            orderState={displayedOrder.state}
            orderStateTitle={displayedOrder.state_title}
          />
        </Box>
      </Box>
      <ResponsiveManageDeliveryPanel
        currentOrderDeliveryDate={displayedOrder.analytics.delivery_date}
        currentOrderDeliveryDateLabel={displayedOrder.title}
        hasSaveDeliveryDateError={hasSaveDeliveryDateError}
        hasSkipOrderError={hasSkipOrderError && !isSkipDiscountModalOpen}
        isManageDeliveryExpanded={isManageDeliveryExpanded}
        isSavingDeliveryDate={isSavingDeliveryDate}
        isSkipBoxDisabled={isSkippingOrder || isFetchingSkipDiscount}
        isSkipOrder={isSkippedOrder}
        onSaveDeliveryDate={handleSaveDeliveryDate}
        onSkipBoxClick={handleSkipBoxRequestClick}
        onTrackBoxClick={handleTrackBoxClick}
        orderDeliveryDetails={orderDeliveryDetails}
        orderState={displayedOrder.state}
      />
      <SkipOrderDiscountModal
        discount={skipDiscount}
        hasApplyDiscountError={hasApplySkipDiscountError}
        hasSkipOrderError={hasSkipOrderError}
        isOpen={isSkipDiscountModalOpen}
        isApplyingDiscount={isApplyingSkipDiscount}
        isSkipping={isSkippingOrder}
        modalName={`my-home-skip-order-discount-modal-${displayedOrder.id}`}
        onApply={handleApplySkipDiscountClick}
        onClose={handleSkipDiscountModalClose}
        onSkip={() => {
          void handleConfirmSkipBoxClick()
        }}
        onView={handleSkipDiscountModalView}
      />
    </OrderTileContainer>
  )
}
